import { useSelector, useDispatch } from "react-redux";
import { getLang, getUserHashId, setIsDirty } from "store/userSlice";
import { api } from "helpers/axios";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { emptyHtml, errorMessage, getTextFromHtml } from "helpers/common";
import * as Sentry from "@sentry/react";
import ListPagination from "components/ui/ListPagination";

import { Link, useOutletContext } from "react-router-dom";

import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { en, de } from 'yup-locales';
import { useForm } from "react-hook-form";
import { toast } from "helpers/toast";

import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { AsyncTypeahead } from "react-bootstrap-typeahead";
import sanitize from "helpers/sanitize";
import { tableResize } from "helpers/tableResize";
import TextTruncate from "components/ui/TextTruncate";
import TableScrollbar from "components/EmployeeManagement/TableScrollbar";
import ErrorAlert from "components/ui/ErrorAlert";
import { useTranslation } from "translation/useTranslation";
import EditorInput from "components/ui/EditorInput";
import ExpandableHtml from "components/ui/ExpandableHtml";
import { helpTextIcon } from "helpers/helpText";
import { fetchFields, getAllCustomNames, getAllFields } from "store/fieldsSlice";
import { Button, Field, Input, NativeSelect, Select } from "components/ui";

function TeamObjectiveContent({ activeTeamObjective, setActiveTeamObjective, settings, renderEvaluationLevelCol, form, selectedSetting, onSubmit, onDelete, setError, loading, onSubmitEvaluation = null, handleDisplayAdd = null }) {

  const { translate, editableTranslate } = useTranslation();

  const { helps } = useOutletContext();

  const userHashId = useSelector(getUserHashId);

  const { register, errors, getValues, setValue, watch, handleSubmit, isDirty, reset, watchEvaluators } = form;

  const watchDescription = watch('description');
  const watchDetails = watch('details');
  const watchLkSetting = watch('balanced_scorecard_setting_hash_id');
  const settingOptions = (settings || []).map(setting => ({ value: setting.hash_id, label: setting.name }));

  const [descriptionError, setDescriptionError] = useState(false);
  const [detailsError, setDetailsError] = useState(false);
  const [commentError, setCommentError] = useState(false);

  const [mode, setMode] = useState(activeTeamObjective?.hash_id ? 'view' : 'edit');

  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOptions, setSearchOptions] = useState([]);
  const [selectedSearchUser, setSelectedSearchUser] = useState([]);

  const [evaluationLevel, setEvaluationLevel] = useState(null);
  const [comment, setComment] = useState(null);

  const [evaluationErrors, setEvaluationErrors] = useState([]);

  const ownedByCurrentUser = useMemo(() => {
    return activeTeamObjective?.creator?.hash_id === userHashId;
  }, [activeTeamObjective]);

  const hasEmployees = useMemo(() => {
    return activeTeamObjective?.employees?.length > 0;
  }, [activeTeamObjective]);

  useEffect(() => {
    if (activeTeamObjective?.hash_id) setMode('view');
  }, [activeTeamObjective]);

  useEffect(() => {
    if (mode === 'edit') {
      setDescriptionError(false);
      setDetailsError(false);
    } else if (mode === 'evaluation') {
      setCommentError(false);
    }
  }, [mode]);

  useEffect(() => {
    if (activeTeamObjective?.hash_id) {
      const levels = [];
      if (selectedSetting) {
        selectedSetting?.individual_levels.forEach(l => {
          const level = l[selectedSetting.individual_levels_key + 'Level'];
          const curLevel = activeTeamObjective?.levels?.filter(al => al.level == level) || [];

          levels.push({
            level,
            definition: (activeTeamObjective?.balanced_scorecard_setting?.hash_id === selectedSetting.hash_id && curLevel.length > 0) ? curLevel[0].definition : ''
          });
        });
      }

      reset({
        balanced_scorecard_setting_hash_id: activeTeamObjective?.balanced_scorecard_setting?.hash_id || null,
        name: activeTeamObjective?.name || null,
        description: activeTeamObjective?.description || null,
        details: activeTeamObjective?.details || null,
        levels,
        evaluators: activeTeamObjective?.evaluators
      });

      setEvaluationLevel(activeTeamObjective?.evaluation_level);
      setComment(activeTeamObjective?.comment);
    }
  }, [activeTeamObjective, mode]);

  useEffect(() => {
    if (selectedSetting) {
      const levels = [];
      selectedSetting?.individual_levels.forEach(l => {
        const level = l[selectedSetting.individual_levels_key + 'Level'];
        const curLevel = activeTeamObjective?.levels?.filter(al => al.level == level) || [];

        levels.push({
          level,
          definition: (activeTeamObjective?.balanced_scorecard_setting?.hash_id === selectedSetting.hash_id && curLevel.length > 0) ? curLevel[0].definition : ''
        });
      });

      setValue('levels', levels);
    }
  }, [selectedSetting]);

  const handleEditorChange = (key, v) => {
    let dirty = true;
    if (emptyHtml(v)) dirty = false;

    setValue(key, v, { shouldDirty: dirty });
  };

  const handleSelectEvaluator = (selected) => {
    if (selected.length === 0) return;

    const currentEvaluators = getValues('evaluators');
    currentEvaluators.push({
      hash_id: selected[0].id,
      first_name: selected[0].first_name,
      last_name: selected[0].last_name
    });

    setValue('evaluators', currentEvaluators);
    setSelectedSearchUser([]);
  };

  const handleRemoveEvaluator = (hashId) => {
    const currentEvaluators = getValues('evaluators');
    const newEvaluators = currentEvaluators.filter(e => e.hash_id !== hashId);

    setValue('evaluators', newEvaluators);
  };

  const handleSearchUser = (roleName) => (query) => {
    setSearchLoading(true);

    const currentEvaluators = getValues('evaluators')?.map(e => e.hash_id);

    api.get('/users', { params: { q: query, in: 'group-setting:' + roleName } }).then(({ data }) => {
      const res = data.data.filter(user => user.hash_id !== userHashId && !currentEvaluators.includes(user.hash_id)).map(user => (
        {
          id: user.hash_id,
          label: [user.first_name, user.last_name].filter(u => u).join(' '),
          first_name: user.first_name,
          last_name: user.last_name
        }
      ));
      setSearchOptions(res);
    }).catch(err => {
      console.error(err);
    }).finally(() => {
      setSearchLoading(false);
    });
  };

  const handleCancel = () => {
    if (activeTeamObjective?.hash_id) setMode('view');
    else handleDisplayAdd();
  };

  const handleClose = () => {
    if (activeTeamObjective?.hash_id) setActiveTeamObjective(null);
    else handleDisplayAdd();
  };

  const handleModeEvaluation = () => {
    if (!activeTeamObjective?.hash_id) return;

    if (!activeTeamObjective?.balanced_scorecard_setting?.hash_id || activeTeamObjective?.levels.length === 0 || !activeTeamObjective?.levels[0]?.definition) {
      setError({ translationKey: 'evaluation.please_complete_team_objective' });
      return;
    }

    setMode('evaluation');
  };

  const renderEvaluationLevelSelect = () => {
    let keyName = selectedSetting.individual_levels_key;
    let arr = selectedSetting.individual_levels;

    return (
      <NativeSelect data-testid="evaluation-level-select" id="team-objective-evaluation-level" className="team-objective-evaluation-select" error={evaluationErrors.includes('evaluationLevel')} onChange={e => setEvaluationLevel(e.target.value)} value={evaluationLevel || ''}>
        <option value=""></option>
        {
          arr?.map((item, i) => {
            return <option key={i}>{item[keyName + 'Level']}</option>
          })
        }
      </NativeSelect>
    );
  };

  const handleSaveEvaluation = () => {
    if (commentError) return;

    const errs = [];
    if (!evaluationLevel) errs.push('evaluationLevel');

    if (selectedSetting?.settings?.individualMandatoryCommentForTeamObjectives === true) {
      if (emptyHtml(comment)) errs.push('comment');
    }

    if (errs.length > 0) {
      setEvaluationErrors(errs);
      return;
    } else setEvaluationErrors([]);

    onSubmitEvaluation({
      evaluation_level: evaluationLevel,
      comment: comment
    }, () => setMode('view'));
  };

  const handleSubmitForm = (form) => {
    if (descriptionError || detailsError) return;

    const cb = () => setMode('view');

    if (mode === 'edit') {
      form.mode = 'normal';
      if (hasEmployees) {
        form.mode = 'evaluators_only';
      }
      onSubmit(form, cb);
    }
  };

  const getLabel = (key, translation_key, fallback) => {
    const sKey = key.replaceAll('.', '-');
    const help = helps && helps.filter(h => h.key === sKey)[0] || null;

    return <>
      {editableTranslate(translation_key, fallback)}
      {help && helpTextIcon(sKey, help.short_text, 'text-normal')}
    </>
  };

  return <form onSubmit={handleSubmit(handleSubmitForm)}>
    <div className="team-objective-header">
      <h5 className="font-weight-bold mb-0">{editableTranslate('evaluation.objective_details')}</h5>
      <div>
        {
          mode === 'edit' && <>
            <Button variant="secondary" onClick={handleCancel} data-testid="cancel-button" className="mr-2">{editableTranslate('button.cancel')}</Button>
            <Button type="submit" variant="primary" loading={loading} data-testid="save-button">{editableTranslate('button.save')}</Button>
          </>
        }
        {
          mode === 'evaluation' && <>
            <Button variant="secondary" onClick={handleCancel} data-testid="cancel-button" className="mr-2">{editableTranslate('button.cancel')}</Button>
            <Button onClick={handleSaveEvaluation} variant="primary" loading={loading} data-testid="save-button">{editableTranslate('button.save')}</Button>
          </>
        }
        {
          (mode !== 'edit' && mode !== 'evaluation') && <>
            <Button variant="ghost" size="inline" className={`mr-1 ${ownedByCurrentUser ? 'text-primary' : ''}`} onClick={() => ownedByCurrentUser ? setMode('edit') : null} data-tooltip-id="team-objective-action-tooltip" data-tooltip-content={translate(ownedByCurrentUser ? 'edit' : 'tooltips.evaluation.only_creator_can_edit')} data-testid="edit-team-objective-button">
              <i className="bi bi-pencil-square text-bigger"></i>
            </Button>
            <Button variant="ghost" size="inline" className={`mr-1 ${ownedByCurrentUser ? 'text-primary' : ''}`} onClick={() => ownedByCurrentUser ? handleModeEvaluation() : null} data-tooltip-id="team-objective-action-tooltip" data-tooltip-content={translate(ownedByCurrentUser ? 'evaluation_mode' : 'tooltips.evaluation.only_creator_can_edit')} data-testid="evaluate-team-objective-button">
              <i className="bi bi-file-earmark-check text-bigger"></i>
            </Button>
            <Button data-testid="delete-team-objective-button" variant="ghost" size="inline" className={`mr-3 ${ownedByCurrentUser ? 'text-primary' : ''}`} data-tooltip-id={ownedByCurrentUser ? 'delete-team-objective' : 'team-objective-action-tooltip'} data-tooltip-content={ownedByCurrentUser ? '' : translate('tooltips.evaluation.only_creator_can_edit')}>
              <i className="bi bi bi-trash text-bigger"></i>
            </Button>
            <span data-testid="close-button" className={`cursor-pointer font-weight-bold balanced-scorecard-close ${mode !== 'view' ? 'v-middle' : ''}`} data-tooltip-id="team-objective-action-tooltip" data-tooltip-content={translate('button.close')} onClick={handleClose}><i className="bi bi-x-lg"></i></span>
          </>
        }

        <ReactTooltip id="team-objective-action-tooltip" place="bottom" />
        <ReactTooltip id={`delete-team-objective`} place="bottom" openOnClick={true} clickable>
          <Button variant="primary" size="sm" onClick={() => onDelete(activeTeamObjective?.hash_id)} data-testid="confirm-button" className="font-weight-normal">{editableTranslate('form.delete.yes')}</Button>
        </ReactTooltip>
      </div>
    </div>

    <div>
      <div className={`team-objective-data-card-div ${mode === 'evaluation' ? 'row' : ''} mb-4`}>
        <div className={mode === 'evaluation' ? `col-6` : ''}>
          <div>
            <span className="text-big font-weight-bold">{editableTranslate('evaluation.basic_details')}</span>
          </div>
          <hr className="mt-2" />
          <div className="mt-3">
            {
              mode === 'edit' ? <>
                <div className="row">
                  <div className="col-6">
                    <div className="form-group">
                      <Field label={editableTranslate('evaluation.objective_name')} error={errors?.name?.message}>
                        <Input type="text" error={!!errors?.name} {...register('name')} readOnly={hasEmployees} data-testid="name-input" />
                      </Field>
                    </div>
                  </div>
                  <div className="col">
                    <div className="form-group">
                      <Field label={getLabel('team_objectives.evaluation.select_balanced_scorecard_setting', 'evaluation.select_balanced_scorecard_setting')} error={errors?.balanced_scorecard_setting_hash_id?.message}>
                        <Select
                          error={!!errors?.balanced_scorecard_setting_hash_id}
                          options={settingOptions}
                          value={settingOptions.find(o => o.value === watchLkSetting) || null}
                          onChange={option => setValue('balanced_scorecard_setting_hash_id', option?.value || '', { shouldDirty: true, shouldValidate: true })}
                          isClearable={settings.length > 1}
                          isDisabled={hasEmployees}
                          placeholder={translate('form.select_placeholder')}
                          data-testid="balanced-scorecard-setting-hash-id-select"
                        />
                      </Field>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-6">
                    <span className="font-weight-bold">{editableTranslate('balanced_scorecard.objective_description')}</span>
                    <EditorInput data-testid="description-input"
                      className={`mt-2 ${hasEmployees ? 'hide-toolbar' : ''}`}
                      value={watchDescription}
                      onChangeCallback={(v) => handleEditorChange('description', v)}
                      validate={(v) => v ? setDescriptionError(true) : setDescriptionError(false)}
                      readOnly={hasEmployees} />
                  </div>
                  <div className="col-6">
                    <span className="font-weight-bold">{editableTranslate('balanced_scorecard.details_milestones')}</span>
                    <EditorInput data-testid="details-input"
                      className={`mt-2 ${hasEmployees ? 'hide-toolbar' : ''}`}
                      value={watchDetails}
                      onChangeCallback={(v) => handleEditorChange('details', v)}
                      validate={(v) => v ? setDetailsError(true) : setDetailsError(false)}
                      readOnly={hasEmployees} />
                  </div>
                </div>
                <div className="levels-div px-3 mt-4">
                  <div className="row">
                    {renderEvaluationLevelCol(selectedSetting, register, errors, hasEmployees)}
                  </div>
                </div>
              </> : <>
                <div className="d-flex mb-2">
                  <div className="card-body-key">{editableTranslate('evaluation.objective_name')}</div>
                  <div className={`card-body-value`}>{activeTeamObjective?.name}</div>
                </div>
                <div className="d-flex mb-2">
                  <div className="card-body-key">{editableTranslate('balanced_scorecard.objective_description')}</div>
                  <div className={`card-body-value w-100`}>
                    <ExpandableHtml html={activeTeamObjective?.description} />
                  </div>
                </div>
                <div className="d-flex">
                  <div className="card-body-key">{editableTranslate('balanced_scorecard.details_milestones')}</div>
                  <div className={`card-body-value w-100`}>
                    <ExpandableHtml html={activeTeamObjective?.details} />
                  </div>
                </div>
              </>
            }
          </div>
        </div>
        {
          mode === 'evaluation' && <div className="col-6 border-left">
            <div>
              <span className="text-big font-weight-bold">{editableTranslate('balanced_scorecard.evaluation_mode')}</span>
            </div>
            <hr className="mt-2" />
            <div className="row">
              <div className="col-auto">
                <Field label={editableTranslate('balanced_scorecard.evaluation_level')} htmlFor="team-objective-evaluation-level">
                  {renderEvaluationLevelSelect()}
                </Field>
              </div>
              <div className="col">
                <Field label={editableTranslate('evaluation.comment')}>
                  <EditorInput data-testid="comment-input"
                    className={`team-objective-evaluation-comment ${evaluationErrors.includes('comment') ? 'is-invalid' : ''}`}
                    value={comment}
                    onChangeCallback={(v) => setComment(v)}
                    validate={(v) => v ? setCommentError(true) : setCommentError(false)} />
                </Field>
              </div>
            </div>
          </div>
        }
      </div>
      <div className="team-objective-data-card-div d-flex">
        <div className="w-50 pr-3">
          <div className="d-flex justify-content-between align-items-center">
            <span className="text-big font-weight-bold">{getLabel('team_objectives.evaluation.share_with', 'evaluation.share_with')}</span>
            {
              mode === 'edit' && <AsyncTypeahead
                className="search-evaluator-input"
                id={activeTeamObjective?.hash_id + '-user-search'}
                onSearch={handleSearchUser('evaluator')}
                onChange={handleSelectEvaluator}
                selected={selectedSearchUser}
                isLoading={searchLoading}
                options={searchOptions}
                placeholder={translate('evaluation.search_by_email_or_full_name')}
                useCache={false}
                renderMenuItemChildren={(option) => {
                  return (
                    <div>
                      <strong>{option.label}</strong>
                    </div>
                  );
                }}
              />
            }
          </div>

          <hr className="mt-2" />
          <div className="scrollable-employees-evaluators-list">
            {
              mode === 'edit' && watchEvaluators?.map((e, i) => <span key={i} className="badge badge-employee-evaluator mr-2 mb-2">
                {[e.first_name, e.last_name].filter(u => u).join(' ')}
                <Button variant="ghost" size="inline" className="ml-2" icon="bi-x-lg" onClick={() => handleRemoveEvaluator(e.hash_id)} data-testid={`remove-evaluator-${e.hash_id}`} />
              </span>)
            }
            {
              mode === 'edit' && watchEvaluators?.length === 0 && <span>-</span>
            }
            {
              mode !== 'edit' && activeTeamObjective?.creator && activeTeamObjective?.creator?.hash_id !== userHashId && <span className="badge badge-employee-evaluator mr-2 mb-2" data-tooltip-id="team-objective-creator-tooltip"><i className="bi bi-person text-smaller"></i> {activeTeamObjective.creator.first_name + ' ' + activeTeamObjective.creator.last_name}</span>
            }
            {
              mode !== 'edit' && activeTeamObjective?.evaluators && activeTeamObjective?.evaluators.filter(e => e.hash_id !== userHashId).map((evaluator, j) => <span key={j} className="badge badge-employee-evaluator mr-2 mb-2">{evaluator.first_name + ' ' + evaluator.last_name}</span>)
            }
            {
              mode !== 'edit' && activeTeamObjective?.evaluators && activeTeamObjective?.evaluators.filter(e => e.hash_id !== userHashId).length === 0 && <span>-</span>
            }
          </div>
        </div>
        <div className="w-50 border-left pl-3">
          <div className="d-flex align-items-center">
            <span className="text-big font-weight-bold">{getLabel('team_objectives.evaluation.employees', 'global.employees', 'Employees')}</span>
            {
              mode === 'edit' && <Input type="text" className="invisible" />
            }
          </div>
          <hr className="mt-2" />
          <div className="scrollable-employees-evaluators-list">
            {
              activeTeamObjective?.employees && activeTeamObjective?.employees.map((employee, j) => <span key={j} className="badge badge-employee-evaluator mr-2 mb-2">{employee.full_name}</span>)
            }
            {
              activeTeamObjective?.employees && activeTeamObjective?.employees.length === 0 && <span>-</span>
            }
          </div>
        </div>
      </div>
    </div>
  </form>;
}

export default function TeamObjectivesOverview() {
  const dispatch = useDispatch();

  const { translate, editableTranslate } = useTranslation();

  const lang = useSelector(getLang);

  const userHashId = useSelector(getUserHashId);

  const fieldCustomNames = useSelector(getAllCustomNames);
  const allFields = useSelector(getAllFields);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [settings, setSettings] = useState(null);

  const [teamObjectives, setTeamObjectives] = useState(null);
  const [activeTeamObjective, setActiveTeamObjective] = useState(null);

  const [displayAdd, setDisplayAdd] = useState(false);
  const [selectedSetting, setSelectedSetting] = useState(null);

  const [sortCols, setSortCols] = useState([['name', 'asc']]);
  const [sortPinCol, setSortPinCol] = useState(null);

  const [resizedCount, setResizedCount] = useState(0);
  const [triggerColWidth, setTriggerColWidth] = useState(0);

  const [pagination, setPagination] = useState({
    page: 1,
    totalRows: 0,
    totalPages: 1,
    perPage: 20
  });

  const headerRef = useRef(null);
  const floatingSeparatorRef = useRef(null);

  const theadRef = useRef(null);

  const tableRef = useRef(null);

  const objSortCols = useMemo(() => {
    const obj = {};

    for (const sc of sortCols) {
      obj[sc[0]] = sc[1];
    }

    return obj;
  }, [sortCols]);

  const schema = yup.object({
    balanced_scorecard_setting_hash_id: yup.string().required().label(fieldCustomNames?.['field.balanced_scorecard_template'] || translate('field.balanced_scorecard_template', 'LK-Vorlage')),
    name: yup.string().required().label(translate('evaluation.objective_name')),
    description: yup.string().notRequired(),
    details: yup.string().notRequired(),
    levels: yup.array().of(yup.object().shape({
      level: yup.string().required(),
      definition: yup.string().required()
    })).required(),
    evaluators: yup.array().of(yup.object().shape({
      hash_id: yup.string().required(),
      first_name: yup.string().notRequired(),
      last_name: yup.string().notRequired()
    })).notRequired()
  });

  const { register, reset, watch, getValues, setValue, handleSubmit, formState: { errors, isDirty } } = useForm({
    resolver: yupResolver(schema)
  });

  const watchLkSetting = watch('balanced_scorecard_setting_hash_id');
  const watchEvaluators = watch('evaluators');

  useEffect(() => {
    yup.setLocale(lang === 'en' ? en : de);
  }, [lang]);

  useEffect(() => {
    if (!allFields) dispatch(fetchFields());

    fetchSettings();

    const theadEl = theadRef.current;
    if (!theadEl) return;

    const headerEl = headerRef.current;
    if (!headerEl) return;

    const floatingEl = floatingSeparatorRef.current;
    if (!floatingEl) return;

    const parentEl = theadEl.parentElement;
    const headerParentEl = headerEl.parentElement;
    const floatingParentEl = floatingEl.parentElement;

    const handleScrollTable = () => {
      const coordinates = parentEl.getBoundingClientRect();
      const navHeight = document.getElementById('navbar-menu')?.offsetHeight || 0;

      const tabsParentCoordinates = headerParentEl.getBoundingClientRect();
      if (tabsParentCoordinates.y < navHeight) {
        headerEl.style.transform = `translate3d(0, ${-tabsParentCoordinates.y + navHeight + 7}px, 0)`;
        theadEl.style.transform = `translate3d(0, ${-coordinates.y + navHeight + headerEl.offsetHeight + 21}px, 0)`;

        floatingParentEl.style.display = 'block';
        floatingEl.style.display = 'block';
        floatingEl.style.transform = `translate3d(0, ${-tabsParentCoordinates.y + navHeight}px, 0)`;
        floatingEl.style.height = (headerEl.offsetHeight + theadEl.offsetHeight + 35) + 'px';
      } else {
        headerEl.style.transform = 'translate3d(0,0,0)';
        theadEl.style.transform = 'translate3d(0,0,0)';

        floatingParentEl.style.display = 'none';
        floatingEl.style.display = 'hidden';
        floatingEl.style.transform = 'translate3d(0,0,0)';
        floatingEl.style.height = '.5rem';
      }
    };
    window.addEventListener('scroll', handleScrollTable);
    return () => window.removeEventListener('scroll', handleScrollTable);
  }, []);

  useEffect(() => {
    if (!watchLkSetting) {
      setSelectedSetting(null);
      setValue('levels', [], { shouldDirty: false });
    } else {
      const temp = settings.filter(s => s.hash_id === watchLkSetting)[0];
      setSelectedSetting(temp);
      // setValue('levels', temp.individual_levels.map(l => ({
      //   level: l[temp.individual_levels_key + 'Level'],
      //   definition: ''
      // })), { shouldDirty: false });
    }
  }, [watchLkSetting]);

  // useEffect(() => {
  //   dispatch(setIsDirty(isDirty));
  // }, [isDirty]);

  useEffect(() => {
    fetchTeamObjectives();
  }, [pagination.page, pagination.perPage, sortCols]);

  useEffect(() => {
    const table = tableRef.current;
    if (!table) return;

    tableResize(table, 'team_objectives', false, {}, {}, setResizedCount);
  }, [teamObjectives, triggerColWidth, displayAdd, activeTeamObjective]);

  useEffect(() => {
    setResizedCount(c => c + 1);
  }, [displayAdd, activeTeamObjective]);

  const fetchSettings = async () => {
    setError(null);
    try {
      const { data, status } = await api.get(`/settings/balanced-scorecards?showSetting=true`);
      if (status === 200) {
        const arr = data.data.filter(s => s.settings.howToAppraisal.includes('individual')).map(s => {
          let key = 'general';
          let grid = s.settings.generalPerformanceLevelGrid;

          if (s.settings?.individualObjectivePerformanceLevelGrid && s.settings?.individualObjectivePerformanceLevelGrid[0]?.individualObjectiveLevel) {
            key = 'individualObjective';
            grid = s.settings.individualObjectivePerformanceLevelGrid;
          }

          return {
            ...s,
            individual_levels_key: key,
            individual_levels: grid
          }
        });

        setSettings(arr);
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch balanced scorecard settings'));
      Sentry.captureException(err);
    }
  };

  const fetchTeamObjectives = async (loading = true, page = null) => {
    if (loading) setLoading(true);
    setError(null);

    if (!page) page = pagination.page;

    try {
      let url = `/employee-balanced-scorecards/team-objectives?limit=${pagination.perPage}&page=${page}`;
      if (sortCols && sortCols.length > 0) {
        url += `&sort=` + sortCols.map(sc => sc[0] + ':' + sc[1]).join(',');
      }
      const { data, status } = await api.get(url);
      if (status === 200) {
        setTeamObjectives(data.data);
        if (activeTeamObjective) {
          for (const item of data.data) {
            if (activeTeamObjective.hash_id === item.hash_id) {
              setActiveTeamObjective(item);
              break;
            }
          }
        }
        const temp = data.meta.pagination;
        setPagination({ page, totalRows: +temp.total, totalPages: +temp.total_pages, perPage: +temp.per_page });
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch team objectives'));
      Sentry.captureException(err);
    }
    setLoading(false);
  };

  const renderEvaluationLevelCol = (setting, register, errors, readOnly = false) => {
    if (!setting) return;

    let keyName = setting.individual_levels_key;
    let arr = setting.individual_levels;

    return (
      <table className="mb-0">
        <thead>
          <tr>
            <th className="text-center">{editableTranslate('balance_scorecard.level')}</th>
            <th className="text-center">{editableTranslate('balance_scorecard.description')}</th>
            <th className="text-center">{editableTranslate('balanced_scorecard.definition')}</th>
          </tr>
        </thead>
        <tbody>
          {
            arr?.map((item, i) => <tr key={i}>
              <td className="text-right pr-2">
                {item[keyName + 'Level']}
              </td>
              <td className="pl-2">{item[keyName + 'Description']}</td>
              <td>
                {
                  !readOnly ? <Input type="text" size="sm" error={!!errors?.levels?.[i]?.definition} {...register(`levels.${i}.definition`)} maxLength={255} /> : <span className="pl-2">{getValues(`levels.${i}.definition`)}</span>
                }
              </td>
            </tr>)
          }
        </tbody>
      </table>
    );
  };

  const handleDisplayAdd = () => {
    const display = !displayAdd;
    if (display) {
      setActiveTeamObjective(null);
    }
    resetEmpty();
    setDisplayAdd(!displayAdd);
  };

  const resetEmpty = () => {
    let balance_scorecard_id = null;

    if (settings.length === 1) {
      balance_scorecard_id = settings[0].hash_id;
    }

    reset({
      balanced_scorecard_setting_hash_id: balance_scorecard_id,
      name: '',
      description: null,
      details: null,
      levels: [],
      evaluators: []
    });
  };

  const onSubmit = async (formData, cb = null) => {
    if (activeTeamObjective) formData.hash_id = activeTeamObjective.hash_id;

    setError(null);
    setLoading(true);
    try {
      const { status } = await api.post(`/employee-balanced-scorecards/team-objectives`, formData);
      if (status === 200) {
        setDisplayAdd(false);
        if (!activeTeamObjective) setActiveTeamObjective(null);
        if (cb) cb();

        fetchTeamObjectives(false);
        toast.success(translate('form.success.saved'));
      }
    } catch (err) {
      setError(errorMessage(err, 'save team objective'));
      Sentry.captureException(err);
    } finally {
      setLoading(false);
    }
  };

  const onSubmitEvaluation = async (formData, cb = null) => {
    if (!activeTeamObjective?.hash_id) return;

    setLoading(true);
    setError(null);
    try {
      const { status } = await api.patch(`/employee-balanced-scorecards/team-objectives/${activeTeamObjective.hash_id}/evaluation`, formData);
      if (status === 200) {
        if (cb) cb();

        fetchTeamObjectives(false);
        toast.success(translate('form.success.saved'));
      }
    } catch (err) {
      setError(errorMessage(err, 'save team objective evaluation'));
      Sentry.captureException(err);
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async (hash_id) => {
    setError(null);
    try {
      const { status } = await api.delete(`/employee-balanced-scorecards/team-objectives/${hash_id}`);
      if (status === 200) {
        setActiveTeamObjective(null);

        fetchTeamObjectives(false);
        toast.success(translate('form.success.saved'));
      }
    } catch (err) {
      setError(errorMessage(err, 'delete team objective'));
      Sentry.captureException(err);
    }
  };

  const handleChangeSortCol = (field) => {
    const newSortCols = [...sortCols];
    let found = false;
    for (let i = 0; i < newSortCols.length; i++) {
      if (newSortCols[i][0] === field) {
        newSortCols[i][1] = (newSortCols[i][1] === 'asc' ? 'desc' : 'asc');
        found = true;
        break;
      }
    }
    if (!found) {
      if (newSortCols.length > 1 || !sortPinCol) newSortCols.pop(); // max 2, so if > 1 or no pin, we remove the last one first before push
      newSortCols.push([field, 'asc']);
    }
    setSortCols(newSortCols);
  };

  const handleSetSortPinCol = (field) => {
    if (sortPinCol === field) {
      setSortPinCol(null);
      setSortCols([sortCols[0]]);
    } else {
      setSortPinCol(field);
      setSortCols(sortCols.filter(sc => sc[0] === field));
    }
  };

  useEffect(() => {
    if (!sortPinCol) setTriggerColWidth(p => p + 1);
  }, [sortCols]);

  const handleClickRow = (obj) => {
    setDisplayAdd(false);
    setActiveTeamObjective(obj);
  };

  return (
    <>
      <div className="col d-flex">
        <div className={`bg-white w-100 p-4 content-card min-height-after-navbar`}>
          <div className="">
            <div className="position-relative" style={{ display: 'none' }}>
              <div ref={floatingSeparatorRef} className="floating-separator"></div>
            </div>
            <div ref={headerRef} className="row position-relative z-3 mb-3">
              <div className="col-auto align-self-center">
                <h4 className="mb-0">{editableTranslate('global.menu.evaluation.team_objectives_overview')}</h4>
              </div>
              <div className="col d-flex justify-content-end">
                {
                  settings && settings.length > 0 && <div className="text-right">
                    <Button variant="secondary" onClick={handleDisplayAdd} data-testid="add-button">
                      <i className="bi bi-plus"></i> {editableTranslate('balanced_scorecard.team_objective')}
                    </Button>
                  </div>
                }
              </div>
            </div>
            {
              error && <div className="mt-3">
                <ErrorAlert error={error} onClose={() => setError(null)} />
              </div>
            }
            <div className={activeTeamObjective || displayAdd ? 'team-objectives-inline' : ''}>
              <div className="team-objectives-left-div">
                <div className={(activeTeamObjective || displayAdd) ? '' : 'table-responsive has-custom-scrollbar'}>
                  <table ref={tableRef} className="table common-table team-objectives-table" data-testid="team-objectives-table">
                    <thead ref={theadRef} className="sticky-top">
                      <tr>
                        <th className="cursor-pointer">
                          <span className="float-right floating-icons">
                            <Button variant="ghost" size="inline" className="p-0 ml-1" icon={`bi-bold ${objSortCols['name'] ? (objSortCols['name'] === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-up v-hidden'}`} onClick={() => handleChangeSortCol('name')} data-testid="sort-name" />
                            {
                              (!activeTeamObjective && !displayAdd && objSortCols['name']) && <Button variant="ghost" size="inline" className="position-relative z-2 p-0 ml-1" icon={`bi-bold ${sortPinCol === 'name' ? 'bi-pin-angle-fill text-primary' : 'bi-pin-angle'}`} onClick={() => handleSetSortPinCol('name')} data-testid="sort-pin-name" />
                            }
                          </span>
                          <span data-testid="header-name" className="th-span" onClick={() => handleChangeSortCol('name')}>
                            {editableTranslate('evaluation.objective_name')}
                            <i className={`bi bi-bold bi-arrow-up invisible ml-1`}></i>
                            <i className={`bi bi-bold bi-pin-angle-fill invisible ml-1`}></i>
                          </span>
                          <div className="th-resize-div" data-field={'name'}></div>
                        </th>
                        {
                          (!activeTeamObjective && !displayAdd) && <Fragment>
                            <th className="cursor-pointer">
                              <span className="float-right floating-icons">
                                <Button variant="ghost" size="inline" className="p-0 ml-1" icon={`bi-bold ${objSortCols['balanced_scorecard_setting'] ? (objSortCols['balanced_scorecard_setting'] === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-up v-hidden'}`} onClick={() => handleChangeSortCol('balanced_scorecard_setting')} data-testid="sort-balanced-scorecard-setting" />
                                {
                                  objSortCols['balanced_scorecard_setting'] && <Button variant="ghost" size="inline" className="position-relative z-2 p-0 ml-1" icon={`bi-bold ${sortPinCol === 'balanced_scorecard_setting' ? 'bi-pin-angle-fill text-primary' : 'bi-pin-angle'}`} onClick={() => handleSetSortPinCol('balanced_scorecard_setting')} data-testid="sort-pin-balanced-scorecard-setting" />
                                }
                              </span>
                              <span data-testid="header-balanced-scorecard-setting" className="th-span" onClick={() => handleChangeSortCol('balanced_scorecard_setting')}>
                                {editableTranslate('evaluation.objective_balanced_scorecard_setting')}
                                <i className={`bi bi-bold bi-arrow-up invisible ml-1`}></i>
                                <i className={`bi bi-bold bi-pin-angle-fill invisible ml-1`}></i>
                              </span>
                              <div className="th-resize-div" data-field={'balanced_scorecard_setting'}></div>
                            </th>
                            <th className="cursor-pointer">
                              <span className="float-right floating-icons">
                                <Button variant="ghost" size="inline" className="p-0 ml-1" icon={`bi-bold ${objSortCols['share_with'] ? (objSortCols['share_with'] === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-up v-hidden'}`} onClick={() => handleChangeSortCol('share_with')} data-testid="sort-share-with" />
                                {
                                  objSortCols['share_with'] && <Button variant="ghost" size="inline" className="position-relative z-2 p-0 ml-1" icon={`bi-bold ${sortPinCol === 'share_with' ? 'bi-pin-angle-fill text-primary' : 'bi-pin-angle'}`} onClick={() => handleSetSortPinCol('share_with')} data-testid="sort-pin-share-with" />
                                }
                              </span>
                              <span data-testid="header-share-with" className="th-span" onClick={() => handleChangeSortCol('share_with')}>
                                {editableTranslate('evaluation.share_with')}
                                <i className={`bi bi-bold bi-arrow-up invisible ml-1`}></i>
                                <i className={`bi bi-bold bi-pin-angle-fill invisible ml-1`}></i>
                              </span>
                              <div className="th-resize-div" data-field={'share_with'}></div>
                            </th>
                            <th className="cursor-pointer">
                              <span className="float-right floating-icons">
                                <Button variant="ghost" size="inline" className="p-0 ml-1" icon={`bi-bold ${objSortCols['employees'] ? (objSortCols['employees'] === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-up v-hidden'}`} onClick={() => handleChangeSortCol('employees')} data-testid="sort-employees" />
                                {
                                  objSortCols['employees'] && <Button variant="ghost" size="inline" className="position-relative z-2 p-0 ml-1" icon={`bi-bold ${sortPinCol === 'employees' ? 'bi-pin-angle-fill text-primary' : 'bi-pin-angle'}`} onClick={() => handleSetSortPinCol('employees')} data-testid="sort-pin-employees" />
                                }
                              </span>
                              <span data-testid="header-employees" className="th-span" onClick={() => handleChangeSortCol('employees')}>
                                {editableTranslate('global.employees')}
                                <i className={`bi bi-bold bi-arrow-up invisible ml-1`}></i>
                                <i className={`bi bi-bold bi-pin-angle-fill invisible ml-1`}></i>
                              </span>
                              <div className="th-resize-div" data-field={'employees'}></div>
                            </th>
                          </Fragment>
                        }
                      </tr>
                    </thead>
                    <tbody>
                      {
                        displayAdd && <tr className="selected-row">
                          <td colSpan={5}>&nbsp;</td>
                        </tr>
                      }
                      {
                        (!loading && teamObjectives) && teamObjectives.map((obj, i) => <tr key={i} onClick={() => handleClickRow(obj)} className={activeTeamObjective?.hash_id === obj?.hash_id ? 'selected-row' : `table-row`} data-testid={`row-${obj?.name}`}>
                          <td><TextTruncate text={obj?.name} resizedCount={resizedCount} /></td>
                          {
                            (!activeTeamObjective && !displayAdd) && <Fragment>
                              <td className="td-html">
                                <TextTruncate text={obj?.balanced_scorecard_setting?.name} resizedCount={resizedCount} />
                              </td>
                              <td className="text-truncate">
                                {
                                  (obj?.creator && obj?.creator?.hash_id !== userHashId) && <span className="badge badge-employee-evaluator mr-2" data-tooltip-id="team-objective-creator-tooltip"><i className="bi bi-person text-smaller"></i> {obj.creator?.first_name + ' ' + obj.creator?.last_name}</span>
                                }
                                {
                                  obj?.evaluators.filter(e => e.hash_id !== userHashId).slice(0, (obj?.creator && obj?.creator?.hash_id !== userHashId) ? 2 : 3).map((evaluator, j) => <span key={j} className="badge badge-employee-evaluator mr-1">{evaluator?.first_name + ' ' + evaluator?.last_name}</span>)
                                }
                                {
                                  obj?.evaluators.filter(e => e.hash_id !== userHashId).length > (obj?.creator && obj?.creator?.hash_id !== userHashId ? 2 : 3) && <span className="badge badge-employee-evaluator mr-1">+{obj.evaluators.filter(e => e.hash_id !== userHashId).length - (obj?.creator && obj?.creator?.hash_id !== userHashId ? 2 : 3)}</span>
                                }
                              </td>
                              <td className="text-truncate">
                                {
                                  obj?.employees.slice(0, 3).map((employee, j) => <span key={j} className="badge badge-employee-evaluator mr-1">{employee.full_name}</span>)
                                }
                                {
                                  obj?.employees.length > 3 && <span className="badge badge-employee-evaluator mr-1">+{obj.employees.length - 3}</span>
                                }
                              </td>
                            </Fragment>
                          }
                        </tr>)
                      }
                      {
                        (loading || !teamObjectives) && <tr>
                          <td colSpan={5}>
                            <div className="d-flex justify-content-center" data-testid="loading">
                              <div className="spinner-border" role="status">
                                <span className="sr-only">Wird geladen...</span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      }

                      {
                        !loading && Array.isArray(teamObjectives) && teamObjectives.length === 0 && <tr>
                          <td colSpan={5} className="text-center" data-testid="empty-state">{editableTranslate('global.no_data')}</td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
                <TableScrollbar tableRef={tableRef} data={teamObjectives} trigger={resizedCount} />
                <ListPagination
                  page={pagination.page}
                  perPage={pagination.perPage}
                  totalPages={pagination.totalPages}
                  totalRows={pagination.totalRows}
                  setPage={(page) => setPagination({ ...pagination, page })}
                  setPerPage={(perPage) => setPagination({ ...pagination, page: 1, perPage })}
                  className="mt-4"
                />
              </div>
              {
                displayAdd && <div className="team-objectives-right-div">
                  <TeamObjectiveContent activeTeamObjective={{}} setActiveTeamObjective={setActiveTeamObjective} settings={settings} renderEvaluationLevelCol={renderEvaluationLevelCol} form={{ register, errors, getValues, setValue, watch, handleSubmit, isDirty, reset, watchEvaluators }} selectedSetting={selectedSetting} onSubmit={onSubmit} onDelete={onDelete} setError={setError} loading={loading} handleDisplayAdd={handleDisplayAdd} />
                </div>
              }
              {
                activeTeamObjective && <div className="team-objectives-right-div">
                  <TeamObjectiveContent activeTeamObjective={activeTeamObjective} setActiveTeamObjective={setActiveTeamObjective} settings={settings} renderEvaluationLevelCol={renderEvaluationLevelCol} form={{ register, errors, getValues, setValue, watch, handleSubmit, isDirty, reset, watchEvaluators }} selectedSetting={selectedSetting} onSubmit={onSubmit} onDelete={onDelete} setError={setError} onSubmitEvaluation={onSubmitEvaluation} loading={loading} />
                </div>
              }
            </div>
          </div>
        </div>

        <ReactTooltip id="team-objective-creator-tooltip" place="bottom" content={translate('tooltips.evaluation.team_objective_creator', 'Erstellende')} />
      </div>
    </>
  );
}
